// areas/settings/survey-question/pages/SurveyQuestionEdit.tsx
import React, { useEffect, useState } from 'react';
import { Button, Form, Message } from 'semantic-ui-react';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { surveyQuestionApi } from '../api/surveyQuestionApi';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'; // ⬅️ added
import { SurveyQuestion, SurveyQuestionRead } from 'store/generated/admin/survey_question';
import SurveyQuestionEditorOutcomes from './SurveyQuestionEditorOutcomes';
import SurveyQuestionEditorPillar from './SurveyQuestionEditorPillar';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { QuestionStatus, QuestionTypes } from '../constants/SurveyQuestionTypes';

const SurveyQuestionEdit: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const location = useLocation();
    const [searchParams] = useSearchParams();
    const passedType =
        (!isEdit && (location.state as any)?.surveyType) ??
        (!isEdit && Number(searchParams.get('type'))) ??
        undefined;

    const [form, setForm] = useState<SurveyQuestion>({
        name: '',
        status: QuestionStatus.ACTIVE,
        survey_type: QuestionTypes.OUTCOMES,
        url_location: null,
        sort_order: 0,
        questions: null,
    });
    const [errors, setErrors] = useState<string | null>(null);

    const { data: loaded, isLoading, error } =
        surveyQuestionApi.useApiAdminV1SurveyQuestionRetrieveQuery({ id: Number(id) }, { skip: !isEdit });

    const [create] = surveyQuestionApi.useApiAdminV1SurveyQuestionCreateMutation();
    const [update] = surveyQuestionApi.useApiAdminV1SurveyQuestionUpdateMutation();

    useEffect(() => {
        if (loaded) {
            const { id: _id, created_at, updated_at, created_by, ...rest } = loaded as SurveyQuestionRead & any;
            setForm({ ...rest });
        }
    }, [loaded]);

    useEffect(() => {
        if (isEdit) return;

        if (passedType === QuestionTypes.OUTCOMES) {
            setForm(prev => ({
                ...prev,
                survey_type: QuestionTypes.OUTCOMES,
                questions: Array.isArray(prev.questions) ? prev.questions : [], // Outcomes expects an array of groups
            }));
        } else if (passedType === QuestionTypes.PILLARS) {
            setForm(prev => ({
                ...prev,
                survey_type: QuestionTypes.PILLARS,
                questions: (prev.questions && typeof prev.questions === 'object' && 'factors' in (prev.questions as any))
                    ? prev.questions
                    : { factors: [] }, // Pillars/EDI expects an object with factors array
            }));
        }
    }, [isEdit, passedType]);

    const setField = (k: keyof SurveyQuestion, v: any) =>
        setForm(prev => ({ ...prev, [k]: v }));

    const handleSubmit = async () => {
        if (!form.name?.trim()) return setErrors('Name is required');
        setErrors(null);
        try {
            if (isEdit) {
                await update({ id: Number(id), surveyQuestion: form }).unwrap();
            } else {
                await create({ surveyQuestion: form }).unwrap();
            }
            navigate(-1);
        } catch {
            setErrors('Failed to save survey question');
        }
    };

    if (isEdit && isLoading) return <LoadingSpinner />;

    if (isEdit && error) {
        return (
            <PageContent>
                <PageHeader title="Edit Survey Question" />
                <PageSection><Message negative content="Failed to load survey question." /></PageSection>
            </PageContent>
        );
    }

    const pageTitle = isEdit
        ? `${loaded?.name}`
        : form.survey_type === QuestionTypes.OUTCOMES
            ? 'Create Outcome Questions'
            : form.survey_type === QuestionTypes.PILLARS
                ? 'Create Pillar Questions'
                : 'Create Survey Question';

    return (
        <PageContent>
            <PageHeader
                title={pageTitle}
                controls={
                    <>
                        <Button onClick={() => navigate(-1)}>Cancel</Button>
                        <Button primary onClick={handleSubmit}>{isEdit ? 'Save' : 'Create'}</Button>
                    </>
                }
                divider
            />

            <PageSection>
                <Form>
                    <Form.Input
                        label="Name"
                        value={form.name}
                        onChange={(_, { value }) => setField('name', value)}
                        required
                    />

                    {form.survey_type === QuestionTypes.OUTCOMES && (
                        <SurveyQuestionEditorOutcomes
                            groups={form.questions}
                            onChange={(next) => setField('questions', next)}
                            isEditable={form.status === QuestionStatus.ACTIVE}
                        />
                    )}

                    {form.survey_type === QuestionTypes.PILLARS && (
                        <SurveyQuestionEditorPillar
                            data={form.questions}
                            onChange={(next) => setField('questions', next)}
                            isEditable={form.status === QuestionStatus.ACTIVE}
                        />
                    )}

                    {errors && <Message negative content={errors} />}
                </Form>
            </PageSection>
        </PageContent>
    );
};

export default SurveyQuestionEdit;
